import React from 'react';
import { graphql } from 'gatsby';
import isNil from 'utils/isNil';
import useGlobalHeader from 'hooks/useGlobalHeader';
import useGlobalFooter from 'hooks/useGlobalFooter';
import SEO, { createSEO } from '../components/SEO';
import WebsiteLayout from '../layouts/websiteLayout';
import CompanyFounderSection from '../components/CompanyPage/CompanyFounderSection';
import CompanyHeroSection from '../components/CompanyPage/CompanyHeroSection';
import CompanyLetterSection from '../components/CompanyPage/CompanyLetterSection';
import CompanyLogoSection from '../components/CompanyPage/CompanyLogoSection';
import CompanyMilestonesSection from '../components/CompanyPage/CompanyMilestonesSection';
import CompanyReadySection from '../components/CompanyPage/CompanyReadySection';
import CompanyTrustSection from '../components/CompanyPage/CompanyTrustSection';
import useCompanyTimeline from '../hooks/useCompanyTimeline';

const CompanyPage = props => {
  const {
    hero,
    letter,
    trustBar,
    companies,
    founders,
    foundersCallToAction,
    inlineCta,
  } = props.data.contentfulCompanyPage;
  const header = useGlobalHeader();
  const footer = useGlobalFooter();
  const milestones = useCompanyTimeline()?.pastMilestones;

  const seo = createSEO(
    {
      ...props.data.contentfulCompanyPage,
      pageCategory: 'Company',
    },
    props
  );

  return (
    <WebsiteLayout header={header} footer={footer} theme="darkHeroHeader" stickyHeader={hero}>
      <SEO {...seo} />
      <CompanyHeroSection
        backgroundImg={!isNil(hero.backgroundImg) ? hero.backgroundImg.fluid.src : ''}
        title={hero.title}
        titleColor="white"
        description={!isNil(hero.description) ? hero.description.description : ''}
        descriptionColor="white"
        video={hero.wistiaVideo}
      />
      <CompanyLetterSection background="#FFF" title={letter.title} letter={letter} />
      <CompanyTrustSection background="#f3fbff" trustedSection={trustBar} />
      <CompanyLogoSection background="#FFF" logos={companies} />
      <CompanyFounderSection
        background="#003954"
        title="Meet our founders."
        buttonTitle={foundersCallToAction.buttonLabel}
        buttonType="secondary"
        buttonLink={foundersCallToAction.url}
        founders={founders}
      />
      <CompanyMilestonesSection background="#F5FCFF" title="Milestones" milestones={milestones} />
      <CompanyReadySection
        backgroundImg={!isNil(inlineCta.backgroundImage) ? inlineCta.backgroundImage.fluid.src : ''}
        title={inlineCta.headline}
        buttonTitle={!isNil(inlineCta.callToAction) ? inlineCta.callToAction[0].buttonLabel : ''}
        colorScheme={inlineCta.colorScheme}
        buttonLinkTo={!isNil(inlineCta.callToAction) ? inlineCta.callToAction[0].url : ''}
      />
    </WebsiteLayout>
  );
};

export default CompanyPage;

export const CompanyPageQuery = graphql`
  query CompanyPageQuery {
    contentfulCompanyPage {
      pageTitle
      slug
      indexable
      metaDescription {
        metaDescription
      }
      socialSharingImage {
        ... on ContentfulWebsiteImage {
          alt
          image {
            file {
              url
            }
          }
        }
      }
      canonicalUrl
      hero {
        ... on ContentfulHero {
          id
          slug
          title
          backgroundColor
          type
          description {
            description
          }
          backgroundImg {
            fluid(quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          wistiaVideo {
            wistiaVideo
          }
        }
      }

      letter {
        ... on ContentfulLetter {
          title
          bodyTop {
            bodyTop
          }
          blockQuote
          bodyBottom {
            bodyBottom
          }
          author
          signature {
            title
            description
            fluid(quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
      }

      trustBar {
        id
        title
        imgs {
          title
          description
          fluid(quality: 100) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
          id
        }
        backgroundColor
      }

      companies {
        title
        description
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }

      founders {
        title
        description
        fluid(quality: 100) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }

      foundersCallToAction {
        ... on ContentfulCallToAction {
          buttonLabel
          url
        }
      }

      milestones {
        ... on ContentfulMilestone {
          title
          iconType
          description
        }
      }

      inlineCta {
        ... on ContentfulConversionPanel {
          backgroundImage {
            fluid(quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          colorScheme
          style
          subheading
          headline
          callToAction {
            ... on ContentfulCallToAction {
              buttonLabel
              url
            }
          }
        }
      }
    }
  }
`;
